/**
 * Joining the target structure to the foundation data, so the table shows what is still MISSING.
 *
 * Before this, an empty year rendered nothing and a partly-filled year rendered only what had been found —
 * which answers "what do we have?" but never "what do we still need?", the question a reporter actually
 * has. With a structure uploaded, the table is the structure: every datapoint appears, filled or blank.
 *
 * The join is by NAME at read time, not by a stored foreign key, and that is deliberate. A datapoint has no
 * id a promoted row could point at — the row is matched against the structure that was active when it was
 * promoted, and the structure can be replaced afterwards. Re-joining on each render means replacing a
 * structure re-renders the table correctly instead of leaving rows pointing at datapoints that no longer
 * exist. What the join cannot recover is the DECISION taken at promotion, which is why the row's own
 * `offSchema` flag is stored separately and is not consulted here.
 */

/** The off-schema group's label. Exported so a caller can key collapse state on it without retyping it. */
export const OFF_SCHEMA_CATEGORY = "Custom";

/**
 * Trim, collapse whitespace, lowercase.
 *
 * Deliberately the same rule as Java's `FoundationNames.normalise`, which is the identity the server
 * matched on when the row was promoted. A looser rule here would join rows the server called off-schema; a
 * stricter one would leave a matched row sitting in the off-schema group, which reads as data loss.
 *
 * The server ALSO has a second, punctuation-flattening pass. This does not, on purpose: that pass can
 * collide two datapoints differing only in punctuation, and the cost of the mismatch is asymmetric. A row
 * that the server matched loosely and this leaves unjoined shows up under Off-schema — visible, and
 * correctable by fixing the wording. A row joined to the wrong datapoint would show a figure under a name
 * it does not belong to, silently.
 */
const normalise = (value) => (value == null ? "" : String(value).trim().replace(/\s+/g, " ").toLowerCase());

/**
 * The render model: the structure's sections with their datapoints, then anything left over.
 *
 * @param {{datapoints: Array}|null} structure the active structure, or null
 * @param {Array} rows the year's foundation data
 * @returns {{
 *   hasStructure: boolean,
 *   groups: Array<{category: string, items: Array<{key: string, target: object|null, rows: Array}>}>,
 *   filled: number,
 *   total: number,
 *   offSchemaCount: number
 * }}
 */
export function buildTargetView(structure, rows) {
  const datapoints = Array.isArray(structure?.datapoints) ? structure.datapoints : [];
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];

  if (datapoints.length === 0) {
    // No structure: the caller falls back to grouping by the row's own category, which is what the table
    // did before this existed. Reported as `hasStructure: false` rather than as an empty group list,
    // because "nothing wanted" and "nothing said" are different states and only one of them means the
    // table should change shape.
    return { hasStructure: false, groups: [], filled: 0, total: 0, offSchemaCount: 0 };
  }

  /*
    Rows keyed by name, MANY per name. One datapoint legitimately holds several rows: the same metric for
    London and for Leeds are two rows differing only in scope, and both belong under the one datapoint.
    Keeping only the first would hide a site's figure entirely.
  */
  const rowsByName = new Map();
  list.forEach((row) => {
    const key = normalise(row.dataKey);
    const existing = rowsByName.get(key);
    if (existing) existing.push(row);
    else rowsByName.set(key, [row]);
  });

  const groups = [];
  const byCategory = new Map();
  let filled = 0;
  const matched = new Set();

  datapoints.forEach((target) => {
    const key = normalise(target.dataKey);
    const targetRows = rowsByName.get(key) || [];
    if (targetRows.length > 0) {
      filled += 1;
      matched.add(key);
    }
    // The section as written in the sheet, and its ORDER is the sheet's order — a reporter reads their own
    // structure in the shape they authored it, so this ordering is content rather than presentation.
    const category = target.category || "Other";
    let group = byCategory.get(category);
    if (!group) {
      group = { category, items: [] };
      byCategory.set(category, group);
      groups.push(group);
    }
    group.items.push({ key: `target-${target.id ?? key}`, target, rows: targetRows });
  });

  // Everything the structure does not account for, in one trailing group. Kept rather than hidden: these
  // rows are in the foundation data because somebody chose to keep them at promotion, and a table that
  // silently omitted them would contradict that choice.
  const leftovers = [];
  rowsByName.forEach((groupRows, key) => {
    if (!matched.has(key)) leftovers.push(...groupRows);
  });
  if (leftovers.length > 0) {
    groups.push({
      category: OFF_SCHEMA_CATEGORY,
      offSchema: true,
      items: leftovers.map((row) => ({ key: `row-${row.id}`, target: null, rows: [row] })),
    });
  }

  return {
    hasStructure: true,
    groups,
    filled,
    total: datapoints.length,
    offSchemaCount: leftovers.length,
  };
}
